import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import useRouter from '@/composables/useRouter'
import { useI18n } from 'vue-i18n-composable'
import useTruncation from '@/composables/useTruncation'
import WidgetBaseComponent from '@/components/widgets/WidgetBaseComponent.vue'
import { Icons } from '@/icons/icons'
import { ActionTypes, checkActionIcon } from '@/store/modules/actions/actionsTypes'
import { URLS } from '@/router/routes/urlRoutes'
import { ContextName } from '@/store/types/storeGlobalTypes'
import { TabNames } from '@/store/modules/tabs/tabsTypes'
import { entity } from '@/store/modules/entities/entitiesTypes'
import usePermissions from '@/composables/usePermissions'
import useSelectedRegister from '@/composables/useSelectedRegister'
import useStage from '@/composables/useStage'
import useGlobalVisionWidgets from '@/components/expedients/ExpedientCourts/composables/useGlobalVisionWidgets'
import LexonTooltipComponent from '@/components/forms/fields/LexonTooltip/LexonTooltipComponent.vue'

// CONSTS

export default /*#__PURE__*/_defineComponent({
  __name: 'ExpedientNextEventsWidget',
  setup(__props) {

const contexts = {
  expedientSchedule: ContextName.EXPEDIENT_SCHEDULE_WIDGET,
  expedientContext: ContextName.EXPEDIENTS
}

const icons = {
  actionCall: Icons.CALL,
  actionMeeting: Icons.MEETING,
  actionOther: Icons.OTHERS_F,
  actionProcedure: Icons.PROCEDURES,
  addButton: Icons.ADD,
  archiveEmpty: Icons.ARCHIVE_EMPTY,
  internalManagement: Icons.INTERNAL_MANAGEMENT,
  task: Icons.TASK
}

// ROUTER
const router = useRouter()

// TRANSLATIONS
const { t } = useI18n()

const text = {
  noEvents: t('components.widgets.expedient_next_events.no_events'),
  explanation: t('components.widgets.expedient_next_events.explanation'),
  date: t('components.widgets.expedient_next_events.date'),
  hour: t('components.widgets.expedient_next_events.hour'),
  duration: t('components.widgets.expedient_next_events.duration')
}

// COMPOSABLES
const { checkEntityPermissionsGetter, userHasPermissionToSaveAtLeastOneActionType } = usePermissions()
const expedientRegister = useSelectedRegister({ entityName: contexts.expedientContext })
const { selectedRegisterId: selectedExpedientId } = expedientRegister
const { expedientStage } = useStage()
const { expedientGlobalVisionAppointments, fetchExpedientGlobalVisionAppointments, resetAppointements } =
  useGlobalVisionWidgets()

const { isTruncatedItems, checkTruncation, setRef } = useTruncation({
  items: computed(() => expedientGlobalVisionAppointments.value?.body || [])
})

// REFS
const widget = ref<any>(null)
const showSpinner = ref(true)

// COMPUTED
const expedientStageStateGetter = computed(() => expedientStage.value)

const allDataLoaded = computed(() => {
  return !!(expedientGlobalVisionAppointments.value && Object.keys(expedientGlobalVisionAppointments.value).length)
})

const contextMenuItems = computed(() => {
  return [
    ...(checkEntityPermissionsGetter(entity.actions.calls.type).canSave
      ? [
          {
            text: t('navigation.drawer-menu-items.calls'),
            iconCss: icons.actionCall,
            actionType: ActionTypes.CALLS
          }
        ]
      : []),
    ...(checkEntityPermissionsGetter(entity.actions.meetings.type).canSave
      ? [
          {
            text: t('navigation.drawer-menu-items.meetings'),
            iconCss: icons.actionMeeting,
            actionType: ActionTypes.MEETINGS
          }
        ]
      : []),
    ...(checkEntityPermissionsGetter(entity.actions.procedures.type).canSave
      ? [
          {
            text: t('navigation.drawer-menu-items.procedures'),
            iconCss: icons.actionProcedure,
            actionType: ActionTypes.PROCEDURES
          }
        ]
      : []),
    ...(checkEntityPermissionsGetter(entity.actions.task.type).canSave
      ? [
          {
            text: t('navigation.drawer-menu-items.task'),
            iconCss: icons.task,
            actionType: ActionTypes.TASK
          }
        ]
      : []),
    ...(checkEntityPermissionsGetter(entity.actions.internalManagement.type).canSave
      ? [
          {
            text: t('navigation.drawer-menu-items.internal_management'),
            iconCss: icons.internalManagement,
            actionType: ActionTypes.INTERNAL_MANAGEMENT
          }
        ]
      : []),
    ...(checkEntityPermissionsGetter(entity.actions.others.type).canSave
      ? [
          {
            text: t('navigation.drawer-menu-items.others_f'),
            iconCss: icons.actionOther,
            actionType: ActionTypes.OTHERS
          }
        ]
      : [])
  ]
})

// METHODS
const fetchEvents = async () => {
  await fetchExpedientGlobalVisionAppointments({
    idExpedient: selectedExpedientId.value,
    source: contexts.expedientSchedule,
    idStage: expedientStageStateGetter.value
  })
  showSpinner.value = false
}

const onContextMenuItemClicked = (actionType: string) => {
  router.goToRouteWithQuery({
    name: `${URLS.EXPEDIENTS}-${URLS.EXPEDIENTS}`,
    params: { selectedRegisterId: selectedExpedientId.value },
    query: {
      tab: TabNames.ACTIONS,
      action: actionType
    }
  })
}

const goToEvent = (event: any) => {
  const routeData = router.resolveQueryRoute({
    name: URLS.AGENDA,
    query: { id: event.id }
  })
  window.open(routeData?.href, '_blank')
}

// WATCHERS
watch(expedientStageStateGetter, () => {
  showSpinner.value = true
  fetchEvents()
})

// HOOKS
onMounted(() => {
  setTimeout(() => {
    fetchEvents()
  })
})

onBeforeUnmount(() => {
  resetAppointements()
})

return { __sfc: true,contexts, icons, router, t, text, checkEntityPermissionsGetter, userHasPermissionToSaveAtLeastOneActionType, expedientRegister, selectedExpedientId, expedientStage, expedientGlobalVisionAppointments, fetchExpedientGlobalVisionAppointments, resetAppointements, isTruncatedItems, checkTruncation, setRef, widget, showSpinner, expedientStageStateGetter, allDataLoaded, contextMenuItems, fetchEvents, onContextMenuItemClicked, goToEvent, WidgetBaseComponent, checkActionIcon, LexonTooltipComponent }
}

})